import React, { useEffect, useRef, useState } from 'react';
import firebase from '../firebaseConfig';
import { CLIP_DELTA_SCHEMA } from '../utils/grade';

const API = process.env.REACT_APP_UPLOAD_API || '';

// Sum incoming DELTA ops into an existing delta list (one entry per key), so the
// clip layer stays a flat, idempotent set of deltas no matter how many feedback
// rounds the user runs. Zeroed-out keys are dropped.
export function mergeOps(existing, incoming) {
  const byKey = new Map((existing || []).filter((o) => o && o.key).map((o) => [o.key, Number(o.delta) || 0]));
  for (const op of incoming || []) {
    if (!op || !op.key || !Number.isFinite(Number(op.delta))) continue;
    byKey.set(op.key, (byKey.get(op.key) || 0) + Number(op.delta));
  }
  return [...byKey.entries()]
    .map(([key, delta]) => ({ key, delta: Math.round(delta * 1000) / 1000 }))
    .filter((o) => o.delta !== 0);
}

const opLabel = (o) => `${o.key} ${o.delta > 0 ? '+' : ''}${o.delta}`;

// Persisted, clip-aware feedback panel. Natural-language notes ("less teal",
// "warmer", "zoom the barrel") become DELTAS layered over each proof's frozen
// per-frame auto-grade — applied to the WHOLE clip or just the SELECTED proof,
// live across every proof. The durable chat log lives on gradeSession.feedback.
export default function ClipFeedbackPanel({
  clip, clipLayer, selectedProof, captureFrames, perceptionText, feedback = [],
  onApplyClip, onApplyProof, disabled,
}) {
  const [scope, setScope] = useState('clip'); // 'clip' | 'proof'
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const scrollRef = useRef(null);
  const configured = !!API;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [feedback, busy]);

  // No selected proof → fall back to clip scope.
  useEffect(() => { if (!selectedProof && scope === 'proof') setScope('clip'); }, [selectedProof, scope]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setErr('');
    setBusy(true);
    try {
      const frames = (await captureFrames?.(selectedProof)) || [];
      const user = firebase.auth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const history = (feedback || []).slice(-8).map((f) => ({ role: f.role, text: f.text }));
      const res = await fetch(`${API}/ai/clip-grade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          message: text,
          scope,
          schema: CLIP_DELTA_SCHEMA,
          clipLayer: scope === 'clip' ? clipLayer : (selectedProof?.override || { ops: [] }),
          perception: perceptionText || '',
          images: frames,
          history,
        }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
        throw new Error(detail || `request failed (${res.status})`);
      }
      const data = await res.json();
      const ops = Array.isArray(data.ops) ? data.ops : [];
      const crop = data.crop || null;
      const reply = data.reply || 'Done.';
      const summary = data.summary || '';
      if (scope === 'clip') {
        const next = { ops: mergeOps(clipLayer?.ops, ops), crop: crop || clipLayer?.crop || null };
        await onApplyClip?.(next, { text, reply, summary, ops });
      } else if (selectedProof) {
        const ov = selectedProof.override || { ops: [], crop: null };
        const next = { ops: mergeOps(ov.ops, ops), crop: crop || ov.crop || null };
        await onApplyProof?.(selectedProof, next, { text, reply, summary, ops });
      }
    } catch (e) {
      setErr(e.message || 'Feedback failed');
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="cs-aiasst is-open" style={{ position: 'static' }}>
      <div className="cs-aiasst-body" style={{ maxHeight: 'none' }}>
        {!configured && <div className="cs-aiasst-warn">Assistant API isn’t configured in this build.</div>}

        <div className="cs-aiasst-tabs">
          <button type="button" className={`cs-aiasst-tab${scope === 'clip' ? ' on' : ''}`} onClick={() => setScope('clip')}>
            Whole clip
          </button>
          <button
            type="button"
            className={`cs-aiasst-tab${scope === 'proof' ? ' on' : ''}`}
            onClick={() => selectedProof && setScope('proof')}
            disabled={!selectedProof}
            title={selectedProof ? `Adjust only frame ${selectedProof.stillGrab?.frameIndex ?? ''}` : 'Select a proof first'}
          >
            This proof{selectedProof ? ` (f${selectedProof.stillGrab?.frameIndex ?? '?'})` : ''}
          </button>
        </div>

        <div className="cs-aiasst-log" ref={scrollRef}>
          {(!feedback || feedback.length === 0) && (
            <div className="cs-aiasst-empty">
              Tell me how to tune the look across this clip — e.g. <i>“a touch less teal”</i>, <i>“warmer overall”</i>,
              <i> “open the shadows”</i>, or <i>“tighter on the barrel”</i>. I keep each frame’s own auto-grade and just
              nudge it, then re-bake every proof so you can see it. Switch to <b>This proof</b> for a one-frame tweak.
            </div>
          )}
          {(feedback || []).map((m, i) => (
            <div key={i} className={`cs-aiasst-msg cs-aiasst-${m.role === 'assistant' ? 'assistant' : 'user'}`}>
              <div className="cs-aiasst-bubble">
                {m.scope && m.scope !== 'clip' && <span className="cs-aiasst-op cs-aiasst-op-preset" style={{ marginRight: 6 }}>proof</span>}
                {m.text}
              </div>
              {m.role === 'assistant' && Array.isArray(m.ops) && m.ops.length > 0 && (
                <div className="cs-aiasst-ops">
                  {m.ops.map((o, k) => <span key={k} className="cs-aiasst-op">{opLabel(o)}</span>)}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="cs-aiasst-msg cs-aiasst-assistant">
              <div className="cs-aiasst-bubble"><span className="cs-spinner" /> Reading the proofs…</div>
            </div>
          )}
        </div>

        {err && <div className="cs-aiasst-err">{err}</div>}

        <div className="cs-aiasst-input">
          <textarea
            rows={2}
            value={input}
            placeholder={scope === 'clip' ? 'Feedback for the whole clip… (Enter to send)' : 'Tweak just this proof… (Enter to send)'}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={busy || !configured || disabled}
          />
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={send} disabled={busy || !configured || !input.trim() || disabled}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
